import React, {useContext, useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {useQuery} from '@tanstack/react-query';
import {orderDetails, linkOrderToManager} from '../../api/customer';
import {GlobalContext, ReducerActionType} from '../../GlobalReducer';
import {ReducerStateInterface} from '../../interfaces/core/ReducerStateInterface';
import {CustomerInterface} from '../../interfaces/customer/CustomerInterface';
import {CustomerManagerInterface} from '../../interfaces/customer/CustomerManagerInterface';
import OrderProductTable from './OrderProductTable';
import Bill from './Bill';
import {formatDate} from '../../utils/Utils';

export default function Order() {
    const {id} = useParams<{ id: string }>();
    const [state, dispatch] = useContext<[ReducerStateInterface, Function]>(GlobalContext);
    const [linkedManagerIds, setLinkedManagerIds] = useState<number[]>([]);

    const {data: order, isLoading, refetch} = useQuery({
        queryKey: ['order', id],
        queryFn: () => orderDetails(id!, state.customer as CustomerInterface | CustomerManagerInterface),
        enabled: !!state.customer && !!id,
        //@ts-ignore
        retry: !window.NOSCRIPT ? false : 1,
    });

    useEffect(() => {
        if (order) {
            dispatch({
                type: ReducerActionType.SET_BREADCRUMBS,
                payload: [
                    {name: 'Личный кабинет', href: '/account'},
                    {name: 'Заказы', href: '/account/order-list'},
                    {name: `Заказ №${order.id}`}
                ]
            });
        }
    }, [order, dispatch]);

    useEffect(() => {
        if (!order) return;

        // Инициализация привязанных менеджеров
        if (order.customer_managers) {
            setLinkedManagerIds(order.customer_managers.map(m => m.id));
        }

        // Обработка позиций заказа
        order.positions.forEach((position) => {
            const hasBills = order.bills && order.bills.length > 0;

            if (hasBills) {
                // Если есть счета
                position.customer_order_count = position.cnt_actual ?? position.cnt;
                position.current_price = position.price_actual;

                if (position.cnt_actual === 0) {
                    position.is_removed = true;
                }
            } else {
                // Если счетов нет
                if (position.cnt_actual === 0) {
                    position.customer_order_count = 0;
                    position.is_removed = true;
                } else {
                    position.customer_order_count = position.cnt_final || position.cnt;
                }
                position.current_price = position.price_actual || position.price;
            }

            // Сохранение предыдущих значений
            position.customer_order_count_prev = position.cnt;
            position.price_prev = position.price;
        });
        console.log(order.positions)
    }, [order]);

    const handleManagerCheckboxChange = async (managerId: number, isChecked: boolean) => {
        if (!order) return;

        try {
            let newLinkedManagerIds: number[];

            if (isChecked) {
                // Добавляем менеджера
                newLinkedManagerIds = [...linkedManagerIds, managerId];
            } else {
                // Убираем менеджера
                newLinkedManagerIds = linkedManagerIds.filter(id => id !== managerId);
            }

            // Обновляем состояние сразу для лучшего UX
            setLinkedManagerIds(newLinkedManagerIds);

            // Отправляем на сервер
            await linkOrderToManager(
                state.customer as CustomerInterface,
                order,
                newLinkedManagerIds
            );

            // Обновляем данные заказа
            await refetch();
        } catch (error: any) {
            alert(`Ошибка: ${error.message || 'Не удалось обновить привязку менеджера'}`);
            // Откатываем изменения в случае ошибки
            await refetch();
        }
    };

    const handleDuplicate = async () => {
        if (!window.confirm('Создать новый заказ на основе этого?')) {
            return;
        }

        try {
            // TODO: Implement duplicate order API call
            // const response = await duplicateOrder(id!, state.customer);
            // window.location.href = '/search';
            alert('Функция дублирования заказа будет реализована');
        } catch (error: any) {
            alert(`Ошибка: ${error.message || 'Не удалось дублировать заказ'}`);
        }
    };

    if (isLoading) {
        return (
            <div className="container mt-4">
                <div className="text-center">
                    <i className="fa fa-spinner fa-spin fa-3x"></i>
                    <p className="mt-3">Загрузка заказа...</p>
                </div>
            </div>
        );
    }

    if (!order) {
        return (
            <div className="container mt-4">
                <div className="alert alert-danger">
                    <h4><i className="icon fa fa-ban"></i> Ошибка!</h4>
                    Заказ не найден
                </div>
            </div>
        );
    }

    return (
        <div className={'order-details'}>
            {/* Header */}
            <h3>
                        <span
                            className={`badge bg-${order.status.label.replace(/default/, 'secondary') || 'secondary'}`}>
                            {order.status.name}
                        </span>
                {' '}
                Заказ №{order.id}
                {' '}
                от {formatDate(order.create_date)}
            </h3>
            {/*<div className="pull-right"> // TODO: Enable duplicate and download features*/}
            {/*    <button*/}
            {/*        type="button"*/}
            {/*        onClick={handleDuplicate}*/}
            {/*        className="btn btn-link mr-3"*/}
            {/*        style={{textDecoration: 'none'}}*/}
            {/*    >*/}
            {/*        <i className="fa fa-copy"></i> создать на основе этого*/}
            {/*    </button>*/}
            {/*    {' '}*/}
            {/*    <a*/}
            {/*        href={`/order/get-excel-by-id/${order.id}/`}*/}
            {/*        target="_blank"*/}
            {/*        rel="noopener noreferrer"*/}
            {/*    >*/}
            {/*        <i className="fa fa-table"></i> скачать заказ*/}
            {/*    </a>*/}
            {/*</div>*/}

            {/* Products table */}
            <div className="box-body">
                <OrderProductTable positions={order.positions}/>
            </div>

            {/* Bills section */}
            {order.bills && order.bills.length > 0 && (
                <>
                    <div id="bills"></div>
                    <div className="bill-table">
                        <h3>Счета: {order.bills.length} шт.</h3>
                        <div className="table-responsive">
                            <table className="table table-condensed">
                                <thead>
                                <tr>
                                    <th>Счет</th>
                                    <th>Позиций</th>
                                    <th>Сумма</th>
                                    <th>Вес/Объем</th>
                                    {/*<th >Объем</th>*/}
                                    <th>Дата</th>
                                    <th>Статус</th>
                                    <th>&nbsp;</th>
                                </tr>
                                </thead>
                                {order.bills.map((bill) => (
                                    <Bill
                                        key={bill.id}
                                        bill={bill}
                                        customer={state.customer as CustomerInterface | CustomerManagerInterface}
                                        onBillApproved={() => refetch()}
                                    />
                                ))}
                            </table>
                        </div>
                    </div>
                </>
            )}

            <hr/>

            {/* Order details and messages */}
            <div className="box-body">
                <div className="row">
                    {/* Order data column */}
                    <div className="col-md-6">
                        <h3>Данные заказа</h3>

                        {order.comment && (
                            <>
                                <label>Комментарий к заказу</label>
                                <div>{order.comment}</div>
                                <p>&nbsp;</p>
                            </>
                        )}

                        {order.history && order.history.length > 0 && (
                            <>
                                <label>История заказа</label>
                                <table className="table">
                                    <thead>
                                    <tr>
                                        <th>Статус</th>
                                        <th>Исполнитель</th>
                                        <th>Дата</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {order.history.map((historyItem: any, index: number) => (
                                        <tr key={index}>
                                            <td>{historyItem.status?.name || '-'}</td>
                                            <td>{historyItem.manager?.name || '-'}</td>
                                            <td>{formatDate(historyItem.create_date)}</td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </>
                        )}

                        {order.customer_account && (
                            <div>
                                <b>Лицевой счет: </b>
                                {order.customer_account.name}
                                {order.customer_account.ship_type && (
                                    <> (доставка: {order.customer_account.ship_type})</>
                                )}
                            </div>
                        )}

                        {order.customer_consignee && (
                            <div>
                                <b>Грузополучатель: </b>
                                {order.customer_consignee.name}
                                {order.customer_consignee.address && (
                                    <>, {order.customer_consignee.address}</>
                                )}
                            </div>
                        )}

                        {order.shipment_date && (
                            <div>
                                <b>Планируемая дата отгрузки: </b>
                                {formatDate(order.shipment_date)}
                            </div>
                        )}

                        {order.shipped_invoices && (
                            <div>
                                <b>Список номеров отгруженных накладных: </b>
                                <br/>
                                {order.shipped_invoices}
                            </div>
                        )}

                        {order.docs && order.docs.length > 0 && (
                            <>
                                <hr/>
                                <label>Документы по заказу</label>
                                <br/>
                                <ul className="list-inline">
                                    {order.docs.map((doc, index) => (
                                        <li key={index}>
                                            <i className="fa fa-file-code-o"></i>{' '}
                                            <a href={doc.href}>{doc.name}</a>
                                        </li>
                                    ))}
                                </ul>
                            </>
                        )}
                    </div>




                    {/* Managers column */}
                    <div className="col-md-6">
                        <h3>Менеджеры, которым доступен заказ</h3>
                        {(state.customer as CustomerInterface)?.customer_managers && (state.customer as CustomerInterface).customer_managers.length > 0 ? (
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th style={{width: '40px'}}>&nbsp;</th>
                                        <th>ФИО</th>
                                        <th>Email</th>
                                        <th>Телефон</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(state.customer as CustomerInterface).customer_managers.map((manager) => (
                                        <tr key={manager.id}>
                                            <td>
                                                <label className="form-check">
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        checked={linkedManagerIds.includes(manager.id)}
                                                        onChange={(e) => handleManagerCheckboxChange(manager.id, e.target.checked)}
                                                    />
                                                    <div className="form-check-icon"></div>
                                                </label>
                                            </td>
                                            <td>{manager.name}</td>
                                            <td>{manager.email}</td>
                                            <td>{manager.phone}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className="alert alert-info">
                                <i className="icon fa fa-info"></i>
                                {' '}
                                Менеджеры не найдены
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}